import { TextButton } from '@twistopayments/design-system/components/atoms/button'
import {
  TickleDarkDesktopIcon,
  TickleLightDesktopIcon,
} from '@twistopayments/design-system/icons/homepage'
import { isNil } from 'ramda'

import type { HeroStaticTransparentComponent } from '@twisto/api-cms'

import { Button, StrapiMedia } from 'homepage/components/atoms'
import { PrimaryDownloadWidget, Promo } from 'homepage/components/molecules'
import { useUrlWithParams } from 'homepage/libs/hooks'

import { HeroDescription } from '../../hero-description'
import { useHeroContext } from '../../hero-provider'
import { HeroTitle } from '../../hero-title'

import * as styles from './static-transparent.styles'

export const StaticTransparent = ({
  title,
  highlightedTitle,
  description,
  descriptionHighlighted,
  bulletList,
  cta,
  additionalButton,
  rightSideImage,
  hasQrCode,
  hasPromo,
  theme = 'light',
}: HeroStaticTransparentComponent) => {
  const { download, promo } = useHeroContext()
  const ctaUrl = useUrlWithParams(cta?.link, cta?.includeUrlParams)
  const additionalButtonUrl = useUrlWithParams(
    additionalButton?.link,
    additionalButton?.includeUrlParams
  )

  return (
    <div css={[styles.content, styles[theme]]}>
      <div css={styles.hero}>
        <div css={styles.heroCell}>
          <div css={styles.cellContent}>
            {title && (
              <HeroTitle highlighted={highlightedTitle} title={title} />
            )}
            {description && (
              <HeroDescription
                description={description}
                highlighted={descriptionHighlighted}
              />
            )}
            {bulletList && (
              <ul css={styles.bulletList}>
                {bulletList.map(
                  (item) =>
                    item.text && (
                      <li key={item.id}>
                        {theme === 'light' ? (
                          <TickleDarkDesktopIcon />
                        ) : (
                          <TickleLightDesktopIcon />
                        )}
                        {item.text}
                      </li>
                    )
                )}
              </ul>
            )}
            {(cta || additionalButton) && (
              <div css={styles.cta}>
                {theme === 'light' ? (
                  <>
                    {cta?.text && cta.link && (
                      <TextButton css={styles.button} href={ctaUrl}>
                        {cta.text}
                      </TextButton>
                    )}
                    {additionalButton?.text && additionalButton.link && (
                      <TextButton
                        color="secondary"
                        css={styles.button}
                        href={additionalButtonUrl}
                      >
                        {additionalButton.text}
                      </TextButton>
                    )}
                  </>
                ) : (
                  <>
                    {cta?.link && cta.text && (
                      <Button
                        key={cta.id}
                        text={cta.text}
                        url={ctaUrl}
                        variant="inverted"
                      />
                    )}
                    {additionalButton?.link && additionalButton.text && (
                      <Button
                        key={additionalButton.id}
                        text={additionalButton.text}
                        url={additionalButtonUrl}
                        variant="outlinedInverted"
                      />
                    )}
                  </>
                )}
              </div>
            )}

            {hasQrCode && download && (
              <PrimaryDownloadWidget isExtendedAllowed={false} {...download} />
            )}
            {hasPromo && promo && <Promo {...promo} />}
          </div>
        </div>
        <figure css={styles.figure}>
          <div css={styles.background}>
            {!isNil(rightSideImage?.url) && (
              <StrapiMedia priority {...rightSideImage} />
            )}
          </div>
        </figure>
      </div>
    </div>
  )
}
